import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchUsers, createUser, updateUser, toggleUserStatus } from '../../store/slices/usersSlice';
import { usersService } from '../../services/usersService';
import DataTable from '../../common/components/DataTable';
import { RoleCheckboxGroup } from '../../common/components';
import { SimplePaginationInfo, UserRole } from '../../types';
import SuccessIcon from '../../common/assets/icons/success.svg';
import ErrorIcon from '../../common/assets/icons/error.svg';
import { toast } from '../../common/utils/toast';

const AddUser: React.FC = () => {
  const dispatch = useAppDispatch();
  const { list: usersList, loading } = useAppSelector((state) => state.users);
  const { user } = useAppSelector((state) => state.auth);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Validation schema
  const validationSchema = yup.object({
    firstname: yup.string().required('First Name is required'),
    lastname: yup.string().required('Last Name is required'),
    userid: yup.string().required('User ID is required'),
    password: yup.string()
      .test('password-required-or-min', 'Password must be at least 6 characters', function (value) {
        if (!isEditMode && !value) {
          return this.createError({ message: 'Password is required' });
        }

        if (value && value.length < 6) {
          return false;
        }

        return true;
      }),
    confirmPassword: yup.string()
      .test('confirm-password-required-or-matches', 'Passwords must match', function (value) {
        const password = this.parent.password;

        if (!isEditMode && !value) {
          return this.createError({ message: 'Confirm Password is required' });
        }

        if (password && value !== password) {
          return false;
        }

        return true;
      }),
    mobilenumber: yup.string().matches(/^[0-9+\-\s()]*$/, 'Invalid phone number format').optional(),
    email: yup.string().email('Invalid email format'),
    roles: yup.array()
      .min(1, 'At least one role must be selected')
      .required('User role is required')
      .test('role-exclusivity', 'Security Guard cannot be selected with Packer or Receiver roles', function (value) {
        if (!value || value.length === 0) return true;

        const hasSecurity = value.includes(UserRole.SECURITY);
        const hasPackerOrReceiver = value.includes(UserRole.PACKER) || value.includes(UserRole.RECEIVER);

        // If Security Guard is selected, no other roles should be selected
        if (hasSecurity && hasPackerOrReceiver) {
          return this.createError({ message: 'Security Guard cannot be selected with Packer or Receiver roles' });
        }

        return true;
      }),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      firstname: '',
      lastname: '',
      userid: '',
      password: '',
      confirmPassword: '',
      mobilenumber: '',
      email: '',
      roles: [UserRole.PACKER], // Default to Packer role as shown in image
    },
  });

  const watchedRoles = watch('roles');

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  // Table columns definition (API fields)
  const columns = [
    {
      key: 'sNo',
      label: 'S.No.',
      sortable: true,
      width: 50,
      render: (_value: any, row: any) => {
        const index = (usersList as any[]).findIndex((user: any) => user.tblid === (row as any).tblid);
        return String(index + 1).padStart(2, '0');
      }
    },
    {
      key: 'firstname',
      label: 'First Name',
      sortable: true,
      width: 90,
    },
    {
      key: 'lastname',
      label: 'Last Name',
      sortable: true,
      width: 90,
    },
    {
      key: 'userid',
      label: 'User ID',
      sortable: true,
      width: 80,
    },
    {
      key: 'mobilenumber',
      label: 'Mobile Number',
      sortable: true,
      width: 120,
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      width: 150,
    },
    {
      key: 'createddt',
      label: 'Reg. Date',
      sortable: true,
      width: 160,
      render: (value: string) => {
        if (!value) return '-';
        try {
          const date = new Date(value);
          const formattedDate = date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: '2-digit'
          });
          const formattedTime = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
          return `${formattedDate} / ${formattedTime}`;
        } catch (error) {
          return '-';
        }
      },
    },
    { key: 'spacer', label: '' },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      width: 100,
      render: (role: string) => {
        if (!role) return '-';
        const key = role.toString().trim().toLowerCase();
        const roleLabels: { [key: string]: string } = {
          admin: 'Admin',
          packer: 'Packer',
          receiver: 'Receiver',
          security: 'Security Guard',
          'security guard': 'Security Guard',
          user: 'User',
        };
        return roleLabels[key] || role;
      },
    },
    {
      key: 'isactive',
      label: 'Status',
      sortable: true,
      width: 60,
      render: (value: string) => {
        if (value === 'Y') {
          return <SuccessIcon width={20} height={20} />;
        }
        return <ErrorIcon width={20} height={20} />;
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (_column: string, _direction: 'asc' | 'desc') => {
    // TODO: Implement sorting logic
  };

  const handleRowClick = async (row: any) => {
    try {
      const details = await usersService.getUserByUserId(row.userid);
      setEditingUser(details);
      setIsEditMode(true);

      // Prepopulate form with API data
      setValue('firstname', details.firstname || '');
      setValue('lastname', details.lastname || '');
      setValue('userid', details.userid || '');
      setValue('mobilenumber', details.mobilenumber || '');
      setValue('email', details.email || '');
      // Map single role string to roles array expected by the checkbox group
      setValue('roles', details.role ? [details.role] : []);
      // Don't populate password fields for security
      setValue('password', '');
      setValue('confirmPassword', '');
    } catch (_e) {
      // Swallow; API client will toast the error
    }
  };

  const onSubmit = async (data: any) => {
    try {
      if (isEditMode && editingUser) {
        const updatePayload: any = {
          userid: data.userid || (editingUser as any).userid,
          firstname: data.firstname || '',
          lastname: data.lastname || '',
          username: data.userid || (editingUser as any).userid,
          mobilenumber: data.mobilenumber || '',
          email: data.email || '',
          role: (data.roles && data.roles[0]) || 'user',
          isactive: (editingUser as any).isactive || 'Y',
        };

        if (data.password) {
          updatePayload.password = data.password;
        }

        // Update existing user (use userid for API)
        await dispatch(updateUser({
          id: (editingUser as any).userid,
          data: updatePayload,
        })).unwrap();
        toast.showSuccess('User updated successfully!');
      } else {
        // Create new user
        await dispatch(createUser({
          userid: data.userid,
          password: data.password,
          firstname: data.firstname || '',
          lastname: data.lastname || '',
          username: data.userid,
          mobilenumber: data.mobilenumber || '',
          email: data.email || '',
          role: (data.roles && data.roles[0]) || 'user',
          isactive: 'Y',
          cretedby: user?.userid || 'SYSTEM',
        })).unwrap();
        toast.showSuccess('User created successfully!');
      }

      // Reset form and exit edit mode
      handleReset();
    } catch (error: any) {
      toast.showError(error?.message || 'Failed to save user');
    }
  };

  const handleReset = () => {
    reset();
    setEditingUser(null);
    setIsEditMode(false);
  };

  const handleActivateDeactivate = async () => {
    if (!editingUser) return;

    try {
      // Use userid for toggle
      await dispatch(toggleUserStatus((editingUser as any).userid)).unwrap();
      toast.showSuccess('User status updated successfully!');
      // Refresh the users list
      dispatch(fetchUsers());
    } catch (error: any) {
      toast.showError(error?.message || 'Failed to update user status');
    }
  };

  const pagination: SimplePaginationInfo = {
    page: currentPage,
    limit: 10,
  };

  return (
    <div className="h-100">
      <div className="row h-100">
        {/* Left Pane - Form (45%) */}
        <div className="col-lg-5 col-md-6">
          <div className="card h-100" style={{ background: '#e9e6ec' }}>
            <div className="card-header bg-primary text-secondary">
              <h6 className="mb-0">ADD USER</h6>
            </div>
            <div className="card-body d-flex flex-column h-100">
              <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column h-100">
                {/* Form Content - Flexible */}
                <div className="">
                  {/* Two Column Layout */}
                  <div className="row">
                    <div className='colmd-12'>
                      <div className='row'>
                        <div className='col-md-6'>
                          {/* First Name */}
                          <div className="mb-2">
                            <label className="form-label text-primary ">FIRST NAME*</label>
                            <input
                              type="text"
                              className={`form-control ${errors.firstname ? 'is-invalid' : ''}`}
                              placeholder="First Name"
                              {...register('firstname')}
                            />
                            {errors.firstname && (
                              <div className="invalid-feedback">{(errors as any).firstname?.message}</div>
                            )}
                          </div>
                        </div>
                        <div className='col-md-6'>
                          {/* Last Name */}
                          <div className="mb-3">
                            <label className="form-label text-primary ">LAST NAME*</label>
                            <input
                              type="text"
                              className={`form-control ${errors.lastname ? 'is-invalid' : ''}`}
                              placeholder="Last Name"
                              {...register('lastname')}
                            />
                            {errors.lastname && (
                              <div className="invalid-feedback">{(errors as any).lastname?.message}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='col-md-12'>
                      <div className='row'>
                        <div className='col-md-6'>
                          {/* User ID */}
                          <div className="mb-2">
                            <label className="form-label text-primary ">USER ID*</label>
                            <input
                              type="text"
                              className={`form-control ${errors.userid ? 'is-invalid' : ''}`}
                              placeholder="User ID"
                              {...register('userid')}
                              disabled={isEditMode}
                            />
                            {errors.userid && (
                              <div className="invalid-feedback">{(errors as any).userid?.message}</div>
                            )}
                          </div>
                        </div>
                        <div className='col-md-6'>
                          {/* Email */}
                          <div className="mb-2">
                            <label className="form-label text-primary ">EMAIL</label>
                            <input
                              type="email"
                              className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                              placeholder="Enter Email"
                              {...register('email')}
                            />
                            {errors.email && (
                              <div className="invalid-feedback">{errors.email.message}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='col-md-12'>
                      <div className='row'>
                        <div className='col-md-6'>
                          {/* Password */}
                          <div className="mb-2">
                            <label className="form-label text-primary ">PASSWORD{!isEditMode ? '*' : ''}</label>
                            <input
                              type="password"
                              className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                              placeholder={isEditMode ? 'Leave blank to keep current password' : 'Password'}
                              {...register('password')}
                            />
                            {errors.password && (
                              <div className="invalid-feedback">{errors.password.message}</div>
                            )}
                          </div>
                        </div>
                        <div className='col-md-6'>

                          {/* Confirm Password */}
                          <div className="mb-2">
                            <label className="form-label text-primary ">CONFIRM PASSWORD{!isEditMode ? '*' : ''}</label>
                            <input
                              type="password"
                              className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                              placeholder={isEditMode ? 'Leave blank to keep current password' : 'Confirm Password'}
                              {...register('confirmPassword')}
                            />
                            {errors.confirmPassword && (
                              <div className="invalid-feedback">{errors.confirmPassword.message}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='col-md-12'>
                      <div className='row'>
                        <div className='col-md-6'>
                          {/* Mobile Number */}
                          <div className="mb-2">
                            <label className="form-label text-primary ">MOBILE NUMBER</label>
                            <input
                              type="text"
                              className={`form-control ${errors.mobilenumber ? 'is-invalid' : ''}`}
                              placeholder="Enter Mobile Number"
                              {...register('mobilenumber')}
                            />
                            {errors.mobilenumber && (
                              <div className="invalid-feedback">{(errors as any).mobilenumber?.message}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='col-md-12'>
                      <div className='row'>
                        <div className='col-md-6'>
                          {/* User Role - Full Width Below Columns */}
                          <div className="mb-4">
                            <label className="form-label text-primary ">USER ROLE*</label>
                            <RoleCheckboxGroup
                              name="roles"
                              value={watchedRoles}
                              onChange={(roles) => setValue('roles', roles)}
                              error={errors.roles?.message}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons - Fixed at Bottom */}
                <div className="d-flex justify-content-between">
                  <div>
                    {isEditMode && (
                      <button
                        type="button"
                        className={`btn ${editingUser?.isactive === 'Y' ? 'btn-danger' : 'btn-success'}`}
                        onClick={handleActivateDeactivate}
                      >
                        {editingUser?.isactive === 'Y' ? 'DEACTIVATE' : 'ACTIVATE'}
                      </button>
                    )}
                  </div>
                  <div className=''>
                    <button
                      type="submit"
                      className="btn btn-primary me-2"
                    >
                      {isEditMode ? 'EDIT USER' : 'ADD USER'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleReset}
                    >
                      CLEAR
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right Pane - Table (55%) */}
        <div className="col-lg-7 col-md-6">
          <div className="card h-100" style={{ background: '#e9e6ec' }}>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                data={usersList}
                loading={loading}
                pagination={pagination}
                onPageChange={handlePageChange}
                onSort={handleSort}
                searchable={false}
                onRowClick={handleRowClick}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
